import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { FileText, CheckCircle, MapPin, Users, TrendingUp } from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

const StatsContainer = styled.section`
  padding: 0 0 4rem;
  position: relative;
  z-index: 2;
  background: var(--background-light);
`;

const StatsGrid = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-top: -2rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    margin-top: -2rem;
  }
`;

const StatCard = styled.div`
  background: var(--white);
  padding: 2rem 1.5rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: var(--transition);
  border: 1px solid var(--border-light);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$gradient};
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
  }

  @media (max-width: 640px) {
    padding: 1.5rem;
    display: flex;
    align-items: center;
    text-align: left;
    gap: 1rem;
  }
`;

const StatIcon = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 1rem;
  background: ${props => props.$bg};
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);
  transition: var(--transition);
  flex-shrink: 0;

  ${StatCard}:hover & {
    transform: scale(1.1) rotate(5deg);
  }

  @media (max-width: 640px) {
    margin: 0;
    width: 48px;
    height: 48px;
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--text-dark);
  margin-bottom: 0.25rem;
  line-height: 1;
  font-family: var(--font-secondary);

  @media (max-width: 640px) {
    font-size: 2rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.95rem;
  color: var(--text-medium);
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const StatTrend = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.35rem 0.75rem;
  background: rgba(0, 150, 57, 0.1);
  color: var(--accent-green);
  border-radius: var(--radius-full);
  font-size: 0.8rem;
  font-weight: 700;
`;

const statVisuals = [
  {
    id: 1,
    icon: FileText,
    gradient: 'linear-gradient(90deg, #002664, #004d8f)',
    bg: 'linear-gradient(135deg, #002664, #004d8f)'
  },
  {
    id: 2,
    icon: CheckCircle,
    gradient: 'linear-gradient(90deg, #009639, #00b347)',
    bg: 'linear-gradient(135deg, #009639, #00b347)'
  },
  {
    id: 3,
    icon: MapPin,
    gradient: 'linear-gradient(90deg, #C8102E, #e52e4e)',
    bg: 'linear-gradient(135deg, #C8102E, #e52e4e)'
  },
  {
    id: 4,
    icon: Users,
    gradient: 'linear-gradient(90deg, #FECA00, #ffd940)',
    bg: 'linear-gradient(135deg, #FECA00, #ffd940)',
    iconColor: '#002664'
  }
];

const useCountUp = (target, duration = 2000, start = false) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!start) return;

    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);

      countRef.current = Math.floor(easeOutQuart * target);
      setCount(countRef.current);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [target, duration, start]);

  return count;
};

const StatItem = ({ stat, isVisible }) => {
  const count = useCountUp(stat.target, 2000, isVisible);
  const Icon = stat.icon;

  return (
    <StatCard $gradient={stat.gradient}>
      <StatIcon $bg={stat.bg} style={stat.iconColor ? { color: stat.iconColor } : undefined}>
        <Icon size={26} />
      </StatIcon>
      <StatContent>
        <StatNumber>
          {count.toLocaleString()}{stat.suffix || ''}
        </StatNumber>
        <StatLabel>{stat.label}</StatLabel>
        <StatTrend>
          <TrendingUp size={14} />
          {stat.trend}
        </StatTrend>
      </StatContent>
    </StatCard>
  );
};

const Stats = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { content } = useSiteContent('home_stats');

  const dynamicItems = Array.isArray(content?.items) ? content.items : [];
  const mergedStats = statVisuals.map((visual, index) => {
    const item = dynamicItems[index] || {};
    const parsedTarget = Number(item.target);

    return {
      ...visual,
      target: Number.isFinite(parsedTarget) ? parsedTarget : 0,
      label: item.label || '',
      suffix: item.suffix || '',
      trend: item.trend || ''
    };
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <StatsContainer ref={sectionRef}>
      <StatsGrid>
        {mergedStats.map(stat => (
          <StatItem key={stat.id} stat={stat} isVisible={isVisible} />
        ))}
      </StatsGrid>
    </StatsContainer>
  );
};

export default Stats;
